/**
 * Communication Adapter Types - Sprint 31
 *
 * Defines the interface for canister communication adapters.
 * Adapters abstract the differences between local proxy and mainnet agent calls.
 */

import { CanisterName } from '../../config/canister-ids';

// ============================================================================
// Request Types
// ============================================================================

/**
 * Options for making a canister call
 */
export interface CanisterCallOptions {
    /** Name of the canister to call */
    canisterName: CanisterName | string;

    /** Method name to invoke */
    methodName: string;

    /** Arguments to pass to the method */
    args: any[];

    /** Optional timeout in milliseconds (default: 30000) */
    timeout?: number;

    /** Whether this is a query call (vs update) */
    isQuery?: boolean;
}

/**
 * Options for raw HTTP calls (used by proxy adapter)
 */
export interface HttpCallOptions {
    /** Full URL to call */
    url: string;

    /** HTTP method */
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';

    /** Request headers */
    headers?: Record<string, string>;

    /** Request body (will be JSON stringified if object) */
    body?: any;

    /** Timeout in milliseconds */
    timeout?: number;
}

// ============================================================================
// Response Types
// ============================================================================

/**
 * Standard response wrapper
 */
export interface CanisterResponse<T> {
    /** Whether the call succeeded */
    success: boolean;

    /** Response data (if successful) */
    data?: T;

    /** Error message (if failed) */
    error?: string;

    /** Raw response (for debugging) */
    raw?: string;
}

/**
 * Result type matching Rust Result<T, E>
 */
export type Result<T, E = string> =
    | { Ok: T; Err?: never }
    | { Err: E; Ok?: never };

// ============================================================================
// Adapter Interface
// ============================================================================

/**
 * Communication adapter interface
 *
 * Implementations:
 * - ProxyAdapter: Uses HTTP proxy server for local development
 * - AgentAdapter: Uses @dfinity/agent for mainnet
 */
export interface CommunicationAdapter {
    /**
     * Name of the adapter (for logging)
     */
    readonly name: string;

    /**
     * Make a canister call (update or query based on options)
     * @param options - Call options
     * @returns Promise resolving to the response
     */
    call<T = any>(options: CanisterCallOptions): Promise<T>;

    /**
     * Make a query call (read-only, faster)
     * @param options - Call options
     * @returns Promise resolving to the response
     */
    query<T = any>(options: CanisterCallOptions): Promise<T>;

    /**
     * Make an update call (modifies state)
     * @param options - Call options
     * @returns Promise resolving to the response
     */
    update<T = any>(options: CanisterCallOptions): Promise<T>;

    /**
     * Check if the adapter is ready to make calls
     */
    isReady(): boolean;

    /**
     * Initialize the adapter (e.g., create agent, verify connectivity)
     */
    initialize(): Promise<void>;
}

// ============================================================================
// Error Types
// ============================================================================

/**
 * Error thrown when a canister call fails
 */
export class CanisterCallError extends Error {
    constructor(
        message: string,
        public readonly canisterName: string,
        public readonly methodName: string,
        public readonly cause?: Error
    ) {
        super(`${canisterName}.${methodName}: ${message}`);
        this.name = 'CanisterCallError';
    }
}

/**
 * Error thrown when adapter is not initialized
 */
export class AdapterNotInitializedError extends Error {
    constructor(adapterName: string) {
        super(`${adapterName} adapter is not initialized. Call initialize() first.`);
        this.name = 'AdapterNotInitializedError';
    }
}

/**
 * Error thrown on timeout
 */
export class TimeoutError extends Error {
    constructor(
        public readonly canisterName: string,
        public readonly methodName: string,
        public readonly timeoutMs: number
    ) {
        super(`Call to ${canisterName}.${methodName} timed out after ${timeoutMs}ms`);
        this.name = 'TimeoutError';
    }
}
